import { BarChart, Bar, XAxis, YAxis, Tooltip, Text, ResponsiveContainer } from 'recharts';
import { getChartData, parseChartData, getChartFill, ChartDataCalculationParams } from '../reportUtilities';
import styles from './DistrictChart.module.scss';
import { DistrictDataByType } from '../../../peekapak-types/ReportDataExchangeTypes';
import { SchoolModel } from '../../../peekapak-types/DataProtocolTypes';
import { useEffect, useRef, useState } from 'react';
import { logDebugLevel } from '../../GlobalFunctions';

interface Props {
  data: DistrictDataByType;
  eventToChart: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  schoolList: SchoolModel[];
  topFiveSchools: string[];
}

const MIN_BAR_WIDTH = 20;
const GAP_WIDTH = 5;

const DistrictChart = ({ data, eventToChart, startDate, endDate, schoolList, topFiveSchools }: Props): JSX.Element => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [dataByTimeAndSchool, setDataByTimeAndSchool] = useState(undefined);
  const [barSize, setBarSize] = useState(70);
  const [parsedChartData, setParsedChartData] = useState(undefined);
  const [barKeys, setBarKeys] = useState(undefined);
  const [isParametersSet, setIsParametersSet] = useState(false);

  // ResizeObserver to monitor container width changes
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        setContainerWidth(chartContainerRef.current.getBoundingClientRect().width);
      }
    };

    // Update width on mount
    updateWidth();
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      if (chartContainerRef.current) {
        resizeObserver.unobserve(chartContainerRef.current);
      }
    };
  }, []);

  // Calculate optimal number of bars based on container width and bar width constraints
  useEffect(() => {
    if (containerWidth > 0 && startDate && endDate) {
      const params: ChartDataCalculationParams = {
        data,
        startDate,
        endDate,
        schoolList,
        minBarWidth: MIN_BAR_WIDTH,
        gapWidth: GAP_WIDTH,
        containerWidth,
      };

      const dataByTimeAndSchool = getChartData(params);
      setDataByTimeAndSchool(dataByTimeAndSchool);
      const parsedChartData = dataByTimeAndSchool && parseChartData(dataByTimeAndSchool, eventToChart, topFiveSchools);
      setParsedChartData(parsedChartData);
      const barKeys = parsedChartData && Object.keys(parsedChartData[0]).slice(1);
      setBarKeys(barKeys);
      setIsParametersSet(true);
      logDebugLevel(`DistrictChart: Width: ${containerWidth}`);
    } else {
      setIsParametersSet(false);
    }
  }, [containerWidth, eventToChart]);

  return (
    <div ref={chartContainerRef} className={styles.container}>
      {isParametersSet && (
        <ResponsiveContainer width='100%' height={500}>
          <BarChart width={900} height={300} data={parsedChartData}>
            <XAxis
              label={{
                value: '',
                offset: 0,
                dy: 15,
              }}
              dataKey='name'
              interval={0}
              tick={<CustomXAxisTick />}
              height={45}
            />
            <YAxis />
            <Tooltip />
            {barKeys &&
              barKeys.map((key, index) => (
                <Bar
                  dataKey={key}
                  key={key}
                  stackId='a'
                  fill={getChartFill(key === 'Other' ? 5 : index)}
                  barSize={70}
                ></Bar>
              ))}
          </BarChart>
        </ResponsiveContainer>
      )}
      <h3 className={styles.chartLabel}>Period beginning:</h3>
    </div>
  );
};

interface TickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
}
const CustomXAxisTick = ({ x, y, payload }: TickProps) => {
  if (payload && payload.value) {
    return (
      <Text fontSize={'14px'} width={75} x={x} y={y} textAnchor='middle' verticalAnchor='start'>
        {payload.value}
      </Text>
    );
  }
  return null;
};

export default DistrictChart;
